/**
 * FireGuard Live - Login Component
 * Страница за влизане в системата
 */
import React, { useState } from 'react';
import { apiService } from '../services/apiWrapper';
import { useLanguage } from '../i18n/LanguageContext';
import './Login.css';

function Login({ onLogin }) {
  const { t, language, changeLanguage } = useLanguage();
  const [isRegister, setIsRegister] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (isRegister) {
        // Регистрация
        if (password !== confirmPassword) {
          setError(t('login.passwordsNotMatch'));
          setLoading(false);
          return;
        }
        
        await apiService.auth.register({
          username,
          email,
          password,
          fullName
        });
        
        setSuccess(t('login.registerSuccess'));
        setIsRegister(false);
        setPassword('');
        setConfirmPassword('');
      } else {
        // Влизане
        const response = await apiService.auth.login(username, password);
        const { token, user } = response.data;

        // Запази в localStorage
        localStorage.setItem('fireguard_token', token);
        localStorage.setItem('fireguard_user', JSON.stringify(user));

        // Извикай callback
        onLogin(user);
      }
    } catch (err) {
      console.error('Registration/Login error:', err);
      
      let errorMessage;
      if (err.code === 'ERR_NETWORK' || err.message?.includes('Network Error') || err.message?.includes('Failed to fetch')) {
        const apiUrl = process.env.NODE_ENV === 'production' ? 'https://app.firesguard.net' : (process.env.REACT_APP_API_URL || 'http://localhost:5000');
        errorMessage = t('login.networkError') || `Мрежова грешка. Проверете дали backend сървърът работи на ${apiUrl}`;
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = t('login.timeoutError') || 'Request took too long. Please try again.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error && typeof err.response.data.error === 'string') {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      } else {
        errorMessage = isRegister ? t('login.registerError') : t('login.loginError');
      }
      
      setError(errorMessage);
      
      if (err.response?.data?.errors) {
        const errors = err.response.data.errors;
        const errorDetails = Object.values(errors).flat().join(', ');
        setError(`${errorMessage}: ${errorDetails}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="language-switcher-login" style={{ position: 'absolute', top: '20px', right: '20px', display: 'flex', gap: '5px', zIndex: 10 }}>
          <button
            type="button"
            className={`btn btn-sm ${language === 'bg' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => changeLanguage('bg')}
            style={{ padding: '5px 10px', fontSize: '12px', cursor: 'pointer', minWidth: '36px' }}
            aria-pressed={language === 'bg'}
            aria-label="Bulgarian"
            title="Български"
          >
            BG
          </button>
          <button
            type="button"
            className={`btn btn-sm ${language === 'en' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => changeLanguage('en')}
            style={{ padding: '5px 10px', fontSize: '12px', cursor: 'pointer', minWidth: '36px' }}
            aria-pressed={language === 'en'}
            aria-label="English"
            title="English"
          >
            EN
          </button>
        </div>
        <div className="login-header">
          <h1 className="login-title">🔥 {t('login.title')}</h1>
          <p className="login-subtitle">{t('login.subtitle')}</p>
        </div>

        <div className="login-tabs">
          <button
            className={!isRegister ? 'active' : ''}
            onClick={() => {
              setIsRegister(false);
              setError('');
              setSuccess('');
            }}
          >
            {t('common.login')}
          </button>
          <button
            className={isRegister ? 'active' : ''}
            onClick={() => {
              setIsRegister(true);
              setError('');
              setSuccess('');
            }}
          >
            {t('common.register')}
          </button>
        </div>

        {error && (
          <div className="alert alert-error login-error-message" role="alert" aria-live="polite">
            <strong>⚠️ Грешка:</strong> {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            ✅ {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          {isRegister && (
            <div className="form-group">
              <label>{t('common.fullName')}</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder={t('login.placeholder.fullName')}
                required={isRegister}
                disabled={loading}
              />
            </div>
          )}

          {isRegister && (
            <div className="form-group">
              <label>{t('common.email')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('login.placeholder.email')}
                required={isRegister}
                disabled={loading}
              />
            </div>
          )}

          <div className="form-group">
            <label>{t('common.username')}</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t('login.placeholder.username')}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>{t('common.password')}</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('login.placeholder.password')}
                required
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
                title={showPassword ? t('login.hidePassword') : t('login.showPassword')}
              >
                {showPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>
          </div>

          {isRegister && (
            <div className="form-group">
              <label>{t('common.confirmPassword')}</label>
              <div className="password-input-wrapper">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('login.placeholder.password')}
                  required={isRegister}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={loading}
                  title={showConfirmPassword ? t('login.hidePassword') : t('login.showPassword')}
                >
                  {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
                </button>
              </div>
            </div>
          )}

          <button 
            type="submit" 
            className="btn btn-primary btn-block"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="spinner-small"></div>
                {isRegister ? t('login.registering') : t('login.loggingIn')}
              </>
            ) : (
              isRegister ? t('common.register') : t('common.login')
            )}
          </button>
        </form>

        <div className="login-footer">
          <p>{t('login.copyright')}</p>
        </div>
      </div>
    </div>
  );
}

export default Login;
